import { formatCurrency } from '@/lib/format'
import type { AreaGroup, AreaGroupPair } from '@/types/api'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import styles from './AreaPricingPage.module.css'

interface PriceTableProps {
  groups: AreaGroup[]
  pairs: AreaGroupPair[]
}

/**
 * What a Cairo booking adds for every pickup group and drop-off group — the
 * rule written out as the table operations will quote from.
 *
 * Every figure is the server's (`pairs`), computed by the same rule a booking
 * is priced by; this only lays them out.
 */
export function PriceTable({ groups, pairs }: PriceTableProps) {
  if (groups.length === 0) return null

  const total = (pickupGroupId: number, dropoffGroupId: number) =>
    pairs.find((pair) => pair.pickupGroupId === pickupGroupId && pair.dropoffGroupId === dropoffGroupId)

  return (
    <Card>
      <CardHeader
        title="What a trip adds"
        description="Rows are where the pickup is, columns where the drop-off is. EGP before VAT, added to every Cairo package."
      />
      <CardBody>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">From ↓ to →</th>
                {groups.map((group) => (
                  <th key={group.id} scope="col">
                    {group.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map((pickup) => (
                <tr key={pickup.id}>
                  <th scope="row">{pickup.name}</th>
                  {groups.map((dropoff) => {
                    const pair = total(pickup.id, dropoff.id)
                    return (
                      <td key={dropoff.id} className={styles.money}>
                        {pair ? (pair.totalMinor > 0 ? formatCurrency(pair.total) : '0') : '—'}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
            <caption className={styles.tableCaption}>
              A pickup or drop-off in no sub-area, or in a sub-area with no group, adds nothing for its end.
            </caption>
          </table>
        </div>
      </CardBody>
    </Card>
  )
}
